import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Calendar as CalendarIcon, Tag } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_OPTIONS = ["new", "scheduled", "completed", "cancelled"];

function StatusBadge({ value }) {
  const map = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    scheduled: "bg-amber-50 text-amber-700 border-amber-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <Badge
      variant="outline"
      className={`${map[value] || map.new} font-medium capitalize`}
      data-testid={`status-badge-${value}`}
    >
      {value}
    </Badge>
  );
}

function BookingsView() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const pageSize = 25;
  const { data, isLoading: loading } = useQuery({
    queryKey: ["bookings", page],
    queryFn: () =>
      api
        .get(`/bookings?skip=${page * pageSize}&limit=${pageSize}`)
        .then((r) => r.data),
  });
  const bookings = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["bookings"] });

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/bookings/${id}?new_status=${newStatus}`);
      toast.success("Status updated");
      refresh();
    } catch {
      toast.error("Could not update status");
    }
  };

  return (
    <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="pf-heading text-xl font-semibold text-slate-900">Bookings</h2>
          <p className="text-sm text-slate-500 mt-0.5" data-testid="bookings-total">
            {total} total
          </p>
        </div>
        <Button variant="outline" onClick={refresh} data-testid="bookings-refresh">
          Refresh
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Preferred</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
            ) : bookings.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400" data-testid="bookings-empty">No bookings yet</TableCell></TableRow>
            ) : bookings.map((b, i) => (
              <TableRow key={b.id} data-testid={`booking-row-${i}`}>
                <TableCell>
                  <div className="font-medium text-slate-900">{b.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {new Date(b.created_at).toLocaleString()}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  <div>{b.phone}</div>
                  <div>{b.email}</div>
                </TableCell>
                <TableCell>{b.service_type}</TableCell>
                <TableCell className="text-sm">
                  <div className="inline-flex items-center gap-1.5 text-slate-700">
                    <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
                    {b.preferred_datetime}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600 max-w-xs truncate">{b.address}</TableCell>
                <TableCell>
                  <Select
                    value={b.status}
                    onValueChange={(v) => updateStatus(b.id, v)}
                  >
                    <SelectTrigger className="w-36 h-9" data-testid={`booking-status-${i}`}>
                      <SelectValue>
                        <StatusBadge value={b.status} />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-500" data-testid="bookings-page-info">
            Page {page + 1} of {totalPages} · Showing {bookings.length} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="bookings-prev"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="bookings-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SpecialForm({ initial, onSubmit, onCancel, busy }) {
  const [form, setForm] = useState(
    initial || { title: "", description: "", discount_label: "", cta_text: "Claim Offer", active: true },
  );
  const update = (f, v) => setForm((s) => ({ ...s, [f]: v }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      className="space-y-4"
      data-testid="special-form"
    >
      <div>
        <Label>Title</Label>
        <Input
          value={form.title}
          onChange={(e) => update("title", e.target.value)}
          placeholder="New Customer Special"
          required
          data-testid="special-input-title"
          className="mt-2 h-11"
        />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
          placeholder="Save 15% on your first service this month."
          required
          rows={3}
          data-testid="special-input-description"
          className="mt-2"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Discount label</Label>
          <Input
            value={form.discount_label}
            onChange={(e) => update("discount_label", e.target.value)}
            placeholder="15% OFF"
            required
            data-testid="special-input-discount"
            className="mt-2 h-11"
          />
        </div>
        <div>
          <Label>CTA text</Label>
          <Input
            value={form.cta_text}
            onChange={(e) => update("cta_text", e.target.value)}
            placeholder="Claim Offer"
            data-testid="special-input-cta"
            className="mt-2 h-11"
          />
        </div>
      </div>
      <div className="flex items-center gap-3 pt-1">
        <Switch
          checked={form.active}
          onCheckedChange={(v) => update("active", v)}
          data-testid="special-input-active"
        />
        <span className="text-sm text-slate-700">Active (visible to customers)</span>
      </div>
      <DialogFooter className="pt-3">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="special-form-cancel">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={busy}
          data-testid="special-form-submit"
          className="bg-slate-900 hover:bg-blue-700 text-white"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function SpecialsView() {
  const queryClient = useQueryClient();
  const { data: specials = [], isLoading: loading } = useQuery({
    queryKey: ["specials-admin"],
    queryFn: () => api.get("/specials?only_active=false").then((r) => r.data),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["specials-admin"] });

  const create = async (payload) => {
    setBusy(true);
    try {
      await api.post("/specials", payload);
      toast.success("Special created");
      setCreateOpen(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const update = async (payload) => {
    setBusy(true);
    try {
      await api.put(`/specials/${editing.id}`, payload);
      toast.success("Special updated");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this special?")) return;
    try {
      await api.delete(`/specials/${id}`);
      toast.success("Special deleted");
      refresh();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="pf-heading text-xl font-semibold text-slate-900">Specials</h2>
          <p className="text-sm text-slate-500 mt-0.5">{specials.length} total</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-special-btn" className="bg-slate-900 hover:bg-blue-700 text-white">
              <Plus className="h-4 w-4 mr-2" /> Add special
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New special</DialogTitle>
            </DialogHeader>
            <SpecialForm
              onSubmit={create}
              onCancel={() => setCreateOpen(false)}
              busy={busy}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-6">
        {loading ? (
          <p className="text-slate-400 text-sm">Loading…</p>
        ) : specials.length === 0 ? (
          <p className="text-slate-400 text-sm" data-testid="specials-empty-admin">No specials yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {specials.map((s, i) => (
              <div
                key={s.id}
                data-testid={`special-admin-${i}`}
                className="border border-slate-100 rounded-xl p-5 bg-slate-50/50 relative"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">
                      <Tag className="h-3 w-3" /> {s.discount_label}
                    </div>
                    <h3 className="pf-heading mt-3 text-lg font-semibold text-slate-900">{s.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{s.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge
                      variant="outline"
                      className={s.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}
                    >
                      {s.active ? "Active" : "Hidden"}
                    </Badge>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(s)}
                    data-testid={`special-edit-${i}`}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(s.id)}
                    data-testid={`special-delete-${i}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit special</DialogTitle>
          </DialogHeader>
          {editing && (
            <SpecialForm
              initial={editing}
              onSubmit={update}
              onCancel={() => setEditing(null)}
              busy={busy}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const APP_STATUS = ["new", "contacted", "approved", "declined"];

function ApplicationsView() {
  const queryClient = useQueryClient();
  const { data, isLoading: loading } = useQuery({
    queryKey: ["applications"],
    queryFn: () => api.get("/contractor-applications").then((r) => r.data),
  });
  const apps = data?.items || [];
  const total = data?.total || 0;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["applications"] });

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/contractor-applications/${id}?new_status=${newStatus}`);
      toast.success("Status updated");
      refresh();
    } catch {
      toast.error("Could not update status");
    }
  };

  const statusColor = (s) =>
    ({
      new: "bg-blue-50 text-blue-700 border-blue-200",
      contacted: "bg-amber-50 text-amber-700 border-amber-200",
      approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
      declined: "bg-slate-100 text-slate-600 border-slate-200",
    }[s] || "bg-blue-50 text-blue-700 border-blue-200");

  return (
    <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="pf-heading text-xl font-semibold text-slate-900">
            Contractor Applications
          </h2>
          <p className="text-sm text-slate-500 mt-0.5" data-testid="applications-total">
            {total} total
          </p>
        </div>
        <Button variant="outline" onClick={refresh} data-testid="applications-refresh">
          Refresh
        </Button>
      </div>

      <div className="p-6">
        {loading ? (
          <p className="text-slate-400 text-sm">Loading…</p>
        ) : apps.length === 0 ? (
          <p className="text-slate-400 text-sm" data-testid="applications-empty">
            No applications yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {apps.map((a, i) => (
              <div
                key={a.id}
                data-testid={`application-card-${i}`}
                className="border border-slate-100 rounded-xl p-5 bg-slate-50/50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="pf-heading text-lg font-semibold text-slate-900">
                      {a.name}
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(a.created_at).toLocaleString()}
                    </p>
                  </div>
                  <Badge variant="outline" className={`${statusColor(a.status)} capitalize`}>
                    {a.status}
                  </Badge>
                </div>
                <div className="mt-3 text-sm text-slate-700 space-y-1">
                  <p><span className="text-slate-500">Phone:</span> {a.phone}</p>
                  <p><span className="text-slate-500">Email:</span> {a.email}</p>
                  <p><span className="text-slate-500">City:</span> {a.city}</p>
                  <p><span className="text-slate-500">Trades:</span> {a.trades?.join(", ") || "-"}</p>
                  <p><span className="text-slate-500">Years:</span> {a.years_experience ?? "-"}</p>
                  <p><span className="text-slate-500">Tools:</span> {a.has_tools || "-"} · <span className="text-slate-500">Vehicle:</span> {a.vehicle || "-"} · <span className="text-slate-500">Insured:</span> {a.insured || "-"}</p>
                  <p><span className="text-slate-500">Availability:</span> {a.availability?.join(", ") || "-"}</p>
                  {a.why_join && (
                    <p className="mt-2 text-slate-600 italic">“{a.why_join}”</p>
                  )}
                </div>
                <div className="mt-4">
                  <Select
                    value={a.status}
                    onValueChange={(v) => updateStatus(a.id, v)}
                  >
                    <SelectTrigger className="w-44 h-9" data-testid={`application-status-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {APP_STATUS.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("pf_admin_token") : null;

  useEffect(() => {
    if (!token) {
      nav("/admin/login");
      return;
    }
    api.get("/auth/me").catch(() => {
      localStorage.removeItem("pf_admin_token");
      nav("/admin/login");
    });
  }, [nav, token]);

  const logout = () => {
    localStorage.removeItem("pf_admin_token");
    localStorage.removeItem("pf_admin_email");
    nav("/admin/login");
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 overflow-hidden">
              <img src="/phoenix-icon.png" alt="" className="h-full w-full object-cover" />
            </span>
            <span className="pf-heading text-lg font-semibold text-slate-900">
              The Phoenix Fix · Admin
            </span>
          </Link>
          <Button variant="ghost" onClick={logout} data-testid="admin-logout">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <Tabs defaultValue="bookings">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="specials" data-testid="tab-specials">Specials</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings" className="mt-6">
            <BookingsView />
          </TabsContent>
          <TabsContent value="specials" className="mt-6">
            <SpecialsView />
          </TabsContent>
          <TabsContent value="applications" className="mt-6">
            <ApplicationsView />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
